import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Table, Button, Modal, Form, Input, Select, InputNumber, Spin, Alert, Space, Switch, DatePicker, Tabs, Tag } from 'antd';
import moment from 'moment';
import { PlusOutlined, EditOutlined, SearchOutlined } from '@ant-design/icons';
import { customBaseUrl, httpGet, httpPost, httpPut } from '../../store/http';
import { ProfileContext } from '../../store/context/ProfileContext';
import { NotificationManager } from 'react-notifications';

const { Option } = Select;
const { TabPane } = Tabs;

const FeesSettings = ({ canCreate = true }) => {
  const { profile } = useContext(ProfileContext);
  const [fees, setFees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [isFeeTypeModalVisible, setIsFeeTypeModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [feeTypeForm] = Form.useForm();
  const [feeTypes, setFeeTypes] = useState([]); // For dropdown

  const [feeTypesData, setFeeTypesData] = useState([]); // For table
  const [feeTypesLoading, setFeeTypesLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('1');
  const [editingFeeTypeRecord, setEditingFeeTypeRecord] = useState(null);
  const [feesSearchText, setFeesSearchText] = useState('');
  const [feeTypesSearchText, setFeeTypesSearchText] = useState('');

  const filteredFees = useMemo(() => {
    const q = feesSearchText.trim().toLowerCase();
    if (!q) return fees;
    return (fees || []).filter((row) => {
      const haystack = [
        row.id,
        row.feeName,
        row.feeUnit,
        row.appliesToChannel,
        row.customerCategory,
        row.minAmount,
        row.maxAmount,
        row.feeValue,
        row.maxFeeCap,
      ]
        .filter((v) => v != null)
        .map((v) => String(v).toLowerCase())
        .join(' ');
      return haystack.includes(q);
    });
  }, [fees, feesSearchText]);

  const filteredFeeTypes = useMemo(() => {
    const q = feeTypesSearchText.trim().toLowerCase();
    if (!q) return feeTypesData;
    return (feeTypesData || []).filter((row) => {
      const haystack = [
        row.id,
        row.feeCode,
        row.feeName,
        row.feeCategory,
        row.description,
      ]
        .filter((v) => v != null)
        .map((v) => String(v).toLowerCase())
        .join(' ');
      return haystack.includes(q);
    });
  }, [feeTypesData, feeTypesSearchText]);

  const fetchFees = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token') || localStorage.getItem('loanAdminToken');
      const response = await httpGet(`${customBaseUrl.tranv2}/api/v1/admin/fees/configurations`, null, false, false, null, null);
      if (response?.success) {
        setFees(response.data);
      } else {
        setError('Failed to fetch fee settings.');
      }
    } catch (err) {
      setError('An error occurred while fetching fee settings.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFees();
  }, []);

  const fetchFeeTypesForDropdown = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('loanAdminToken');
      const response = await httpGet(`${customBaseUrl.tranv2}/api/v1/admin/fees/types`, null, false, true, token);
      if (response?.success && Array.isArray(response.data)) {
        setFeeTypes(response.data);
      } else {
        console.error('Failed to fetch fee types for dropdown or data is not an array.');
      }
    } catch (err) {
      console.error('An error occurred while fetching fee types for dropdown.', err);
    }
  };

  const fetchFeeTypesForTable = async () => {
    try {
      setFeeTypesLoading(true);
      const token = localStorage.getItem('token') || localStorage.getItem('loanAdminToken');
      const response = await httpGet(`${customBaseUrl.tranv2}/api/v1/admin/fees/types`, null, true, false, token, false);
      if (response?.success && Array.isArray(response.data)) {
        setFeeTypesData(response.data);
      } else {
        setError('Failed to fetch fee types for table.');
      }
    } catch (err) {
      setError('An error occurred while fetching fee types for table.');
      console.error(err);
    } finally {
      setFeeTypesLoading(false);
    }
  };

  useEffect(() => {
    if (isModalVisible) {
      fetchFeeTypesForDropdown();
    }
  }, [isModalVisible]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key === '2' && feeTypesData.length === 0) {
      fetchFeeTypesForTable();
    }
  };

  const handleCreate = async (values) => {
    try {
      const payload = {
        ...values,
        effectiveFrom: values.effectiveFrom ? values.effectiveFrom.toISOString() : null,
        effectiveTo: values.effectiveTo ? values.effectiveTo.toISOString() : null,
        createdBy: profile?.email ?? 'admin',
        thirdPartyFee: values.thirdPartyFee || 0,
        customerCategory: values.customerCategory,
      };
      const response = await httpPost(`${customBaseUrl.tranv2}/api/v1/admin/fees/configurations`, payload, false, null, false);
      if (response?.success) {
        NotificationManager.success('Fee setting created successfully!');
        setIsModalVisible(false);
        fetchFees();
        form.resetFields();
      } else {
        NotificationManager.error(response?.message || 'Failed to create fee setting.');
      }
    } catch (err) {
      NotificationManager.error('An error occurred while creating the fee setting.');
      console.error(err);
    }
  };

  const handleUpdate = async (values) => {
    try {
      const payload = {
        ...values,
        id: editingRecord.id,
        effectiveFrom: values.effectiveFrom ? values.effectiveFrom.toISOString() : null,
        effectiveTo: values.effectiveTo ? values.effectiveTo.toISOString() : null,
        createdBy: profile?.email ?? 'admin',
        thirdPartyFee: values.thirdPartyFee || 0,
        customerCategory: values.customerCategory,
      };
      const response = await httpPut(`${customBaseUrl.tranv2}/api/v1/admin/fees/configurations/${editingRecord.id}`, payload, false, null, false);
      if (response?.success) {
        NotificationManager.success('Fee setting updated successfully!');
        setIsModalVisible(false);
        setEditingRecord(null);
        fetchFees();
        form.resetFields();
      } else {
        NotificationManager.error(response?.message || 'Failed to update fee setting.');
      }
    } catch (err) {
      NotificationManager.error('An error occurred while updating the fee setting.');
      console.error(err);
    }
  };

  const handleCreateFeeType = async (values) => {
    try {
      const response = await httpPost(`${customBaseUrl.tranv2}/api/v1/admin/fees/types`, values, false, null, false);
      if (response?.success) {
        NotificationManager.success('Fee type created successfully!');
        setIsFeeTypeModalVisible(false);
        feeTypeForm.resetFields();
        fetchFeeTypesForTable(); // Refresh table
        fetchFeeTypesForDropdown(); // To refresh the dropdown in the other modal
      } else {
        NotificationManager.error(response?.message || 'Failed to create fee type.');
      }
    } catch (err) {
      NotificationManager.error('An error occurred while creating the fee type.');
      console.error(err);
    }
  };

  const handleUpdateFeeType = async (values) => {
    try {
      const payload = { ...values };
      const response = await httpPut(`${customBaseUrl.tranv2}/api/v1/admin/fees/types/${editingFeeTypeRecord.id}`, payload, null, false, false);
      if (response?.success) {
        NotificationManager.success('Fee type updated successfully!');
        setIsFeeTypeModalVisible(false);
        setEditingFeeTypeRecord(null);
        fetchFeeTypesForTable(); // Refresh table
        feeTypeForm.resetFields();
      } else {
        NotificationManager.error(response?.message || 'Failed to update fee type.');
      }
    } catch (err) {
      NotificationManager.error('An error occurred while updating the fee type.');
      console.error(err);
    }
  };

  const handleToggleStatus = async (record) => {
    try {
      const response = await httpPut(`${customBaseUrl.tranv2}/api/v1/admin/fees/configurations/${record.id}/toggle-status`, {}, false, null, false);
      if (response?.success) {
        NotificationManager.success('Fee status updated successfully!');
        fetchFees();
      } else {
        NotificationManager.error(response?.message || 'Failed to update fee status.');
      }
    } catch (err) {
      NotificationManager.error('An error occurred while updating the fee status.');
      console.error(err);
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      ...record,
      feeTypeId: record.feeTypeId || record.feeType?.id,
      effectiveFrom: record.effectiveFrom ? moment(record.effectiveFrom) : null,
      effectiveTo: record.effectiveTo ? moment(record.effectiveTo) : null,
    });
    setIsModalVisible(true);
  };

  const handleFeeTypeEdit = (record) => {
    setEditingFeeTypeRecord(record);
    feeTypeForm.setFieldsValue(record);
    setIsFeeTypeModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingRecord(null);
    form.resetFields();
  };

  const handleFeeTypeModalCancel = () => {
    setIsFeeTypeModalVisible(false);
    setEditingFeeTypeRecord(null);
    feeTypeForm.resetFields();
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id' },
    { title: 'Fee Name', dataIndex: 'feeName', key: 'feeName' },
    { title: 'Min Amount', dataIndex: 'minAmount', key: 'minAmount' },
    { title: 'Max Amount', dataIndex: 'maxAmount', key: 'maxAmount' },
    { title: 'Fee Value', dataIndex: 'feeValue', key: 'feeValue' },
    { title: '3rd Party Fee', dataIndex: 'thirdPartyFee', key: 'thirdPartyFee', render: (val) => val || 0 },
    { title: 'Profit', dataIndex: 'profit', key: 'profit', render: (val) => val || 0 },
    { title: 'Max Fee Cap', dataIndex: 'maxFeeCap', key: 'maxFeeCap' },
    { title: 'Fee Unit', dataIndex: 'feeUnit', key: 'feeUnit' },
    {
      title: 'Category',
      dataIndex: 'customerCategory',
      key: 'customerCategory',
      render: (cat) => cat ? <Tag color="blue">{cat}</Tag> : <Tag color="default">N/A</Tag>
    },
    { title: 'Channel', dataIndex: 'appliesToChannel', key: 'appliesToChannel' },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (isActive ? 'Active' : 'Inactive'),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            Edit
          </Button>
          <Switch
            checked={record.isActive}
            onChange={() => handleToggleStatus(record)}
          />
        </Space>
      ),
    },
  ];

  const feeTypesColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id' },
    { title: 'Fee Code', dataIndex: 'feeCode', key: 'feeCode' },
    { title: 'Fee Name', dataIndex: 'feeName', key: 'feeName' },
    { title: 'Fee Category', dataIndex: 'feeCategory', key: 'feeCategory' },
    { title: 'Description', dataIndex: 'description', key: 'description' },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (isActive ? 'Active' : 'Inactive'),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button icon={<EditOutlined />} onClick={() => handleFeeTypeEdit(record)}>
          Edit
        </Button>
      ),
    },
  ];

  if (error) return <Alert message="Error" description={error} type="error" showIcon />;

  return (
    <div>
      <Tabs defaultActiveKey="1" onChange={handleTabChange}>
        <TabPane tab="Fee Configurations" key="1">
          {loading ? <Spin tip="Loading Fee Settings..." /> : (
            <>
              <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                {canCreate && (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      setEditingRecord(null);
                      form.resetFields();
                      setIsModalVisible(true);
                    }}
                  >
                    Create New Fee Setting
                  </Button>
                )}
                <Input
                  placeholder="Search by fee name, unit, channel, category, amount..."
                  prefix={<SearchOutlined />}
                  value={feesSearchText}
                  onChange={(e) => setFeesSearchText(e.target.value)}
                  allowClear
                  style={{ maxWidth: 380, flex: 1 }}
                />
              </div>
              <Table dataSource={filteredFees} columns={columns} rowKey="id" />
            </>
          )}
        </TabPane>
        <TabPane tab="Fee Types" key="2">
          {feeTypesLoading ? <Spin tip="Loading Fee Types..." /> : (
            <>
              <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                {canCreate && (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      setEditingFeeTypeRecord(null);
                      feeTypeForm.resetFields();
                      setIsFeeTypeModalVisible(true);
                    }}
                  >
                    Create New Fee Type
                  </Button>
                )}
                <Input
                  placeholder="Search by fee code, name, category, description..."
                  prefix={<SearchOutlined />}
                  value={feeTypesSearchText}
                  onChange={(e) => setFeeTypesSearchText(e.target.value)}
                  allowClear
                  style={{ maxWidth: 380, flex: 1 }}
                />
              </div>
              <Table dataSource={filteredFeeTypes} columns={feeTypesColumns} rowKey="id" />
            </>
          )}
        </TabPane>
      </Tabs>

      <Modal
        title={editingRecord ? 'Edit Fee Setting' : 'Create New Fee Setting'}
        visible={isModalVisible}
        onCancel={handleCancel}
        onOk={() => form.submit()}
        okText={editingRecord ? 'Update' : 'Create'}
      >
        <Form form={form} layout="vertical" onFinish={editingRecord ? handleUpdate : handleCreate}>
          <Form.Item name="feeTypeId" label="Fee Type" rules={[{ required: true }]}>
            <Select placeholder="Select a fee type" onChange={(value, option) => {
              form.setFieldsValue({ feeName: option.children });
            }}>
              {feeTypes.map(type => <Option key={type.id} value={type.id}>{type.feeName.replace(/_/g, ' ')}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="feeName" label="Fee Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="minAmount" label="Minimum Amount" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="maxAmount" label="Maximum Amount" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="feeValue" label="Fee Value" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="feeUnit" label="Fee Unit" rules={[{ required: true }]}>
            <Select placeholder="Select a fee unit">
              <Option value="FIXED_AMOUNT">FIXED AMOUNT</Option>
              <Option value="PERCENTAGE">PERCENTAGE</Option>
            </Select>
          </Form.Item>
          <Form.Item name="appliesToChannel" label="Applies to Channel">
            <Select placeholder="Select a channel">
              <Option value="WEB">WEB</Option>
              <Option value="MOBILE">MOBILE</Option>
              <Option value="POS">POS</Option>
              <Option value="API">API</Option>
              <Option value="ALL">ALL</Option>
            </Select>
          </Form.Item>
          <Form.Item name="customerCategory" label="Customer Category">
            <Select placeholder="Select a category">
              <Option value="A">Category A</Option>
              <Option value="B">Category B</Option>
              <Option value="C">Category C</Option>
            </Select>
          </Form.Item>
          <Form.Item name="thirdPartyFee" label="Third Party Fee">
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="maxFeeCap" label="Max Fee Cap">
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          {/* <Form.Item name="penaltyCount" label="Penalty Count">
            <InputNumber style={{ width: '100%' }} />
          </Form.Item> */}
          <Form.Item name="effectiveFrom" label="Effective From">
            <DatePicker showTime />
          </Form.Item>
          <Form.Item name="effectiveTo" label="Effective To">
            <DatePicker showTime />
          </Form.Item>
          <Form.Item name="isActive" label="Is Active" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title={editingFeeTypeRecord ? 'Edit Fee Type' : 'Create New Fee Type'}
        visible={isFeeTypeModalVisible}
        onCancel={handleFeeTypeModalCancel}
        onOk={() => feeTypeForm.submit()}
        okText={editingFeeTypeRecord ? 'Update' : 'Create'}
      >
        <Form form={feeTypeForm} layout="vertical" onFinish={editingFeeTypeRecord ? handleUpdateFeeType : handleCreateFeeType} initialValues={{ isActive: true }}>
          <Form.Item name="feeCode" label="Fee Code" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="feeName" label="Fee Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="feeCategory" label="Fee Category" rules={[{ required: true }]}>
            <Select placeholder="Select a fee category">
              <Option value="TRANSFER_FEES">TRANSFER FEES</Option>
              <Option value="SERVICE_FEES">SERVICE FEES</Option>
              <Option value="CARD_FEES">CARD FEES</Option>
              <Option value="POS_FEES">POS FEES</Option>
              <Option value="B2B_FEES">B2B FEES</Option>
              <Option value="COMMUNICATION_FEES">COMMUNICATION FEES</Option>
              <Option value="LOAN_FEES">LOAN FEES</Option>
            </Select>
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea />
          </Form.Item>
          <Form.Item name="isActive" label="Is Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default FeesSettings;
